import { memo, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { renderSummaryCellContent, resolveAlign } from '../column-types';
import { ALIGN_CLASS, DENSITY_CELL, type Density } from '../styles';
import type { DataGridColumn, DataGridSummaryItem } from '../types';

export interface GroupHeaderInfo<T> {
  level: number;
  key: string;
  path: string[];
  rows: T[];
  count: number;
}

export interface GroupHeaderRowProps<T> {
  level: number;
  pathLabel: string;
  rows: T[];
  isExpanded: boolean;
  columns: DataGridColumn<T>[];
  summary: DataGridSummaryItem<T>[] | undefined;
  density: Density;
  currency: string;
  path: string[];
  onToggle: () => void;
  renderGroupHeader?: (info: GroupHeaderInfo<T>) => ReactNode;
}

/**
 * Row rendered for a group node.
 *
 * - **Expanded**    : single cell `colSpan={columns.length}` with the
 *   chevron + label. The actual subtotal lives in a dedicated row
 *   right after the group's data rows.
 * - **Collapsed**   : proper multi-cell row that inlines the group's
 *   subtotal values so the user gets a per-group overview at a glance
 *   without having to expand. Matches DevExpress / AG Grid behavior.
 */
function GroupHeaderRowImpl<T>({
  level,
  pathLabel,
  rows,
  isExpanded,
  columns,
  summary,
  density,
  currency,
  path,
  onToggle,
  renderGroupHeader,
}: GroupHeaderRowProps<T>) {
  const indent = level * 20;

  const label = (
    <div className="flex items-center gap-2">
      <ChevronRight
        className={cn(
          'h-4 w-4 text-neutral-400 transition-transform',
          isExpanded && 'rotate-90'
        )}
      />
      <span className="text-sm font-semibold">
        {renderGroupHeader
          ? renderGroupHeader({
              level,
              key: pathLabel,
              path,
              rows,
              count: rows.length,
            })
          : pathLabel}
      </span>
      <span className="text-xs text-neutral-500 font-normal">({rows.length})</span>
    </div>
  );

  const rowClass = cn(
    'border-b border-neutral-200 bg-neutral-50 cursor-pointer select-none',
    'hover:bg-neutral-100 transition-colors'
  );

  // Collapsed without summary → just render the label in a merged cell
  // and skip the per-column layout (cheaper and visually identical).
  if (isExpanded || !summary || summary.length === 0) {
    return (
      <tr onClick={onToggle} className={rowClass}>
        <td
          colSpan={columns.length}
          className={cn(DENSITY_CELL[density], 'text-dark-500')}
          style={{ paddingLeft: `${16 + indent}px` }}
        >
          {label}
        </td>
      </tr>
    );
  }

  // Collapsed WITH summary → proper row with aggregated values inlined.
  return (
    <tr onClick={onToggle} className={cn(rowClass, 'font-medium')}>
      {columns.map((col, colIdx) => {
        const isFirst = colIdx === 0;
        const item = summary.find((s) => s.columnId === col.id);
        return (
          <td
            key={col.id}
            className={cn(
              'align-middle text-dark-500 text-sm',
              DENSITY_CELL[density],
              ALIGN_CLASS[resolveAlign(col)],
              col.cellClassName
            )}
            style={isFirst ? { paddingLeft: `${16 + indent}px` } : undefined}
          >
            {isFirst
              ? label
              : item
                ? renderSummaryCellContent(col, summary, rows, currency)
                : null}
          </td>
        );
      })}
    </tr>
  );
}

export const GroupHeaderRow = memo(GroupHeaderRowImpl) as <T>(
  props: GroupHeaderRowProps<T>
) => ReactNode;
